import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as Dialog from '@radix-ui/react-dialog';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLinkedin,
  faFacebook,
  faInstagram,
  faXTwitter,
  faYoutube,
  faTiktok,
  faPinterest,
  faThreads,
  faGoogle,
  faBluesky,
} from '@fortawesome/free-brands-svg-icons';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';
import { Checkbox } from '@/components/ui/Checkbox';
import { RadioGroup, RadioItem } from '@/components/ui/Radio';
import { Toggle } from '@/components/ui/Toggle';
import { Modal } from '@/components/ui/Modal';
import { Tooltip, TooltipProvider } from '@/components/ui/Tooltip';
import { Icon } from '@/components/ui/Icon';
import { showToast } from '@/components/ui/Toast';

/**
 * Design System Showcase Page
 *
 * PUBLIC page (no auth required) showcasing Ägile Ässets brand guidelines
 * Route: /design-system
 */

interface GradientSwatch {
  name: string;
  css: string;
  style: React.CSSProperties;
}

// ─── Color Palette Reference ─────────────────────────────────────────────────

interface PaletteShade {
  shade: string;
  tailwindClass: string;
  cssVar: string;
  hex: string;
}

interface PaletteFamily {
  label: string;
  shades: PaletteShade[];
  whitelabel: boolean;
  description: string;
}

interface SingleColorEntry {
  label: string;
  tailwindClass: string;
  cssVar: string;
  hex: string;
  description: string;
}

interface UsageConvention {
  context: string;
  classes: string[];
  note: string;
}

const SHADE_FAMILIES: PaletteFamily[] = [
  {
    label: 'Primary (Teal)',
    whitelabel: true,
    description: 'Main UI action color — buttons, links, active states.',
    shades: [
      { shade: '25',  tailwindClass: 'primary-25',  cssVar: '--color-primary-25',  hex: '#e6f7f6' },
      { shade: '50',  tailwindClass: 'primary-50',  cssVar: '--color-primary-50',  hex: '#e0f2f1' },
      { shade: '100', tailwindClass: 'primary-100', cssVar: '--color-primary-100', hex: '#b2dfdb' },
      { shade: '200', tailwindClass: 'primary-200', cssVar: '--color-primary-200', hex: '#80cbc4' },
      { shade: '300', tailwindClass: 'primary-300', cssVar: '--color-primary-300', hex: '#4db6ac' },
      { shade: '400', tailwindClass: 'primary-400', cssVar: '--color-primary-400', hex: '#26a69a' },
      { shade: '500', tailwindClass: 'primary-500', cssVar: '--color-primary-500', hex: '#69C7C1' },
      { shade: '600', tailwindClass: 'primary-600', cssVar: '--color-primary-600', hex: '#00897b' },
      { shade: '700', tailwindClass: 'primary-700', cssVar: '--color-primary-700', hex: '#00796b' },
      { shade: '800', tailwindClass: 'primary-800', cssVar: '--color-primary-800', hex: '#00695c' },
      { shade: '900', tailwindClass: 'primary-900', cssVar: '--color-primary-900', hex: '#004d40' },
    ],
  },
  {
    label: 'Secondary (Dark Teal)',
    whitelabel: true,
    description: 'Subdued / dark variant. Nav backgrounds, footers, dark surfaces.',
    shades: [
      { shade: '50',  tailwindClass: 'secondary-50',  cssVar: '--color-secondary-50',  hex: '#e0f2f1' },
      { shade: '100', tailwindClass: 'secondary-100', cssVar: '--color-secondary-100', hex: '#b2dfdb' },
      { shade: '200', tailwindClass: 'secondary-200', cssVar: '--color-secondary-200', hex: '#80cbc4' },
      { shade: '300', tailwindClass: 'secondary-300', cssVar: '--color-secondary-300', hex: '#4db6ac' },
      { shade: '400', tailwindClass: 'secondary-400', cssVar: '--color-secondary-400', hex: '#26a69a' },
      { shade: '500', tailwindClass: 'secondary-500', cssVar: '--color-secondary-500', hex: '#074F47' },
      { shade: '600', tailwindClass: 'secondary-600', cssVar: '--color-secondary-600', hex: '#063e38' },
      { shade: '700', tailwindClass: 'secondary-700', cssVar: '--color-secondary-700', hex: '#042e2a' },
      { shade: '800', tailwindClass: 'secondary-800', cssVar: '--color-secondary-800', hex: '#031f1c' },
      { shade: '900', tailwindClass: 'secondary-900', cssVar: '--color-secondary-900', hex: '#02100e' },
    ],
  },
  {
    label: 'Coral',
    whitelabel: false,
    description: 'Branding accents only (logo, hero gradients). NOT for interactive UI states.',
    shades: [
      { shade: '50',  tailwindClass: 'coral-50',  cssVar: '--color-coral-50',  hex: '#ffebee' },
      { shade: '100', tailwindClass: 'coral-100', cssVar: '--color-coral-100', hex: '#ffcdd2' },
      { shade: '200', tailwindClass: 'coral-200', cssVar: '--color-coral-200', hex: '#ef9a9a' },
      { shade: '300', tailwindClass: 'coral-300', cssVar: '--color-coral-300', hex: '#e57373' },
      { shade: '400', tailwindClass: 'coral-400', cssVar: '--color-coral-400', hex: '#ef5350' },
      { shade: '500', tailwindClass: 'coral-500', cssVar: '--color-coral-500', hex: '#ff5757' },
      { shade: '600', tailwindClass: 'coral-600', cssVar: '--color-coral-600', hex: '#e53935' },
      { shade: '700', tailwindClass: 'coral-700', cssVar: '--color-coral-700', hex: '#d32f2f' },
      { shade: '800', tailwindClass: 'coral-800', cssVar: '--color-coral-800', hex: '#c62828' },
      { shade: '900', tailwindClass: 'coral-900', cssVar: '--color-coral-900', hex: '#b71c1c' },
    ],
  },
  {
    label: 'Gray',
    whitelabel: false,
    description: 'UI chrome, borders, subtle text, disabled states.',
    shades: [
      { shade: '50',  tailwindClass: 'gray-50',  cssVar: '—', hex: '#f8f9fa' },
      { shade: '100', tailwindClass: 'gray-100', cssVar: '—', hex: '#f0f2f5' },
      { shade: '200', tailwindClass: 'gray-200', cssVar: '—', hex: '#dee2e6' },
      { shade: '300', tailwindClass: 'gray-300', cssVar: '—', hex: '#ced4da' },
      { shade: '400', tailwindClass: 'gray-400', cssVar: '—', hex: '#adb5bd' },
      { shade: '500', tailwindClass: 'gray-500', cssVar: '—', hex: '#7b809a' },
      { shade: '600', tailwindClass: 'gray-600', cssVar: '—', hex: '#6c757d' },
      { shade: '700', tailwindClass: 'gray-700', cssVar: '—', hex: '#495057' },
      { shade: '800', tailwindClass: 'gray-800', cssVar: '—', hex: '#344767' },
      { shade: '900', tailwindClass: 'gray-900', cssVar: '—', hex: '#212529' },
    ],
  },
];

const SINGLE_COLORS: SingleColorEntry[] = [
  { label: 'Off-White',       tailwindClass: 'off-white',       cssVar: '--color-off-white',       hex: '#F6F8F5', description: 'Page & card backgrounds.' },
  { label: 'Soft Mint',       tailwindClass: 'soft-mint',       cssVar: '--color-soft-mint',       hex: '#D5EEEB', description: 'Info boxes, gradient end.' },
  { label: 'Soft Coral',      tailwindClass: 'soft-coral',      cssVar: '--color-soft-coral',      hex: '#FBD3D1', description: '"Changes requested" soft bg.' },
  { label: 'Peach',           tailwindClass: 'peach',           cssVar: '--color-peach',           hex: '#FBCCAE', description: 'Warm accent, gradient end.' },
  { label: 'Light Teal Gray', tailwindClass: 'light-teal-gray', cssVar: '--color-light-teal-gray', hex: '#B9C7C7', description: 'Borders in mint-tone contexts.' },
  { label: 'Sage Gray',       tailwindClass: 'sage-gray',       cssVar: '--color-sage-gray',       hex: '#718080', description: 'Muted body text on light bg.' },
  { label: 'Warm Gray',       tailwindClass: 'warm-gray',       cssVar: '--color-warm-gray',       hex: '#DAD2D1', description: 'Subtle dividers, warm neutrals.' },
  { label: 'Dark Teal',       tailwindClass: 'dark-teal',       cssVar: '—',                       hex: '#074F47', description: 'Alias for secondary-500.' },
];

const STATUS_COLORS: SingleColorEntry[] = [
  { label: 'Success', tailwindClass: 'success', cssVar: '—', hex: '#4CAF50', description: 'Approved, published, active.' },
  { label: 'Warning', tailwindClass: 'warning', cssVar: '—', hex: '#fb8c00', description: 'Pending review, rate-limits.' },
  { label: 'Danger',  tailwindClass: 'danger',  cssVar: '—', hex: '#F44335', description: 'Errors, rejections, destructive.' },
  { label: 'Info',    tailwindClass: 'info',    cssVar: '—', hex: '#1A73E8', description: 'Informational messages.' },
];

const USAGE_CONVENTIONS: UsageConvention[] = [
  { context: 'Approved / Published',    classes: ['bg-primary-500', 'text-success'],       note: 'Use primary for state; success text for icon/label.' },
  { context: 'Pending review',          classes: ['bg-warning', 'text-warning'],            note: 'Status warning amber consistently.' },
  { context: 'Changes requested',       classes: ['bg-danger', 'bg-soft-coral'],            note: 'Hard error = danger; soft/inline = soft-coral.' },
  { context: 'Scheduled',               classes: ['bg-secondary-500', 'bg-light-teal-gray'], note: 'Use secondary for strong; light-teal-gray for subtle.' },
  { context: 'Draft / Not ready',       classes: ['bg-gray-200', 'text-gray-500'],          note: 'Neutral gray; no semantic color.' },
  { context: 'Info / Tips',             classes: ['bg-soft-mint', 'border-light-teal-gray'], note: 'Mint bg + light-teal-gray border for info boxes.' },
  { context: 'Page background',         classes: ['bg-off-white'],                          note: '#F6F8F5 — the standard page canvas.' },
  { context: 'Card / panel surface',    classes: ['bg-white'],                              note: 'White cards on off-white page.' },
  { context: 'Primary CTA',             classes: ['bg-primary-500'],                        note: 'All primary action buttons.' },
  { context: 'Destructive action',      classes: ['bg-danger'],                             note: 'Delete, revoke, hard-cancel.' },
  { context: 'Dark surface text',       classes: ['text-white', 'text-white/80'],           note: 'On secondary-500 or gray-900 backgrounds.' },
  { context: 'Branding accent only',    classes: ['coral-500', 'coral-*'],                  note: 'Logo + hero gradients ONLY. Never interactive states.' },
];

/** Determines whether white text provides readable contrast on a given hex bg */
function needsWhiteText(hex: string): boolean {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  // WCAG relative luminance approximation
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < 0.55;
}

interface ShadeSwatchProps {
  shade: PaletteShade;
  onCopy: (text: string, label: string) => void;
}

const ShadeSwatch = ({ shade, onCopy }: ShadeSwatchProps) => {
  const { t } = useTranslation('common');
  const whiteText = needsWhiteText(shade.hex);

  return (
    <button
      onClick={() => onCopy(`bg-${shade.tailwindClass}`, shade.tailwindClass)}
      aria-label={`${shade.shade} ${shade.hex}, ${t('copy')}: bg-${shade.tailwindClass}`}
      title={`${t('copy')}: bg-${shade.tailwindClass}`}
      className="group relative flex flex-col rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow border border-black/10 text-left w-full"
    >
      {/* Color swatch */}
      <div
        className="w-full"
        style={{ backgroundColor: shade.hex, height: '3.5rem' }}
      />
      {/* Info panel */}
      <div className="bg-white px-2 py-2 flex-1 flex flex-col gap-0.5">
        <span className="text-xs font-semibold text-gray-900">{shade.shade}</span>
        <code className="text-[10px] text-gray-600 font-mono leading-tight block truncate">
          bg-{shade.tailwindClass}
        </code>
        <code className="text-[10px] text-primary-600 font-mono leading-tight block truncate">
          {shade.cssVar}
        </code>
        <span className="text-[10px] text-gray-500 font-mono uppercase">{shade.hex}</span>
      </div>
      {/* Hover: copy hint */}
      <div className={`absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity rounded-lg ${whiteText ? 'bg-black/30' : 'bg-white/50'}`}>
        <span className={`text-xs font-semibold ${whiteText ? 'text-white' : 'text-gray-900'}`}>{t('copy')}</span>
      </div>
    </button>
  );
};

interface SingleColorSwatchProps {
  entry: SingleColorEntry;
  onCopy: (text: string, label: string) => void;
}

const SingleColorSwatch = ({ entry, onCopy }: SingleColorSwatchProps) => (
  <button
    onClick={() => onCopy(`bg-${entry.tailwindClass}`, entry.tailwindClass)}
    title={`Click to copy: bg-${entry.tailwindClass}`}
    className="group relative bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow border border-gray-200 text-left w-full"
  >
    <div className="w-full h-14" style={{ backgroundColor: entry.hex }} />
    <div className="px-3 py-2">
      <p className="text-xs font-semibold text-gray-900 truncate">{entry.label}</p>
      <code className="text-[10px] text-gray-600 font-mono block truncate">bg-{entry.tailwindClass}</code>
      <code className="text-[10px] text-primary-600 font-mono block truncate">{entry.cssVar}</code>
      <span className="text-[10px] text-gray-500 font-mono uppercase">{entry.hex}</span>
      <p className="text-[10px] text-gray-400 mt-1 truncate">{entry.description}</p>
    </div>
  </button>
);

interface ColorPaletteReferenceProps {
  copyToClipboard: (text: string, label: string) => void;
}

const ColorPaletteReference = ({ copyToClipboard }: ColorPaletteReferenceProps) => (
  <div className="space-y-10">
    {/* Shade families */}
    {SHADE_FAMILIES.map(family => (
      <div key={family.label} className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold text-gray-900">{family.label}</h3>
              {family.whitelabel ? (
                <span className="text-[10px] bg-primary-50 text-primary-700 border border-primary-200 px-1.5 py-0.5 rounded-full font-medium">
                  whitelabel-safe
                </span>
              ) : (
                <span className="text-[10px] bg-gray-100 text-gray-500 border border-gray-200 px-1.5 py-0.5 rounded-full">
                  brand-locked
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-0.5">{family.description}</p>
          </div>
        </div>
        <div className="p-4 grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-11 gap-2">
          {family.shades.map(shade => (
            <ShadeSwatch key={shade.shade} shade={shade} onCopy={copyToClipboard} />
          ))}
        </div>
      </div>
    ))}

    {/* Accent single-value colors */}
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-semibold text-gray-900">Accent Colors</h3>
          <span className="text-[10px] bg-gray-100 text-gray-500 border border-gray-200 px-1.5 py-0.5 rounded-full">brand-locked</span>
        </div>
        <p className="text-xs text-gray-500 mt-0.5">Single-value named colors — backdrops, surfaces, soft states.</p>
      </div>
      <div className="p-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {SINGLE_COLORS.map(entry => (
          <SingleColorSwatch key={entry.tailwindClass} entry={entry} onCopy={copyToClipboard} />
        ))}
      </div>
    </div>

    {/* Status colors */}
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-semibold text-gray-900">Status Colors</h3>
          <span className="text-[10px] bg-gray-100 text-gray-500 border border-gray-200 px-1.5 py-0.5 rounded-full">brand-locked</span>
        </div>
        <p className="text-xs text-gray-500 mt-0.5">Semantic status indicators — success, warning, danger, info.</p>
      </div>
      <div className="p-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
        {STATUS_COLORS.map(entry => (
          <SingleColorSwatch key={entry.tailwindClass} entry={entry} onCopy={copyToClipboard} />
        ))}
      </div>
    </div>
  </div>
);

// ─── End Color Palette Reference ─────────────────────────────────────────────

const NAV_SECTIONS = ['brand', 'colors', 'gradients', 'typography', 'buttons', 'forms', 'cards', 'badges', 'icons', 'platforms', 'status', 'avatars', 'media', 'tables', 'tabs', 'tags', 'upload', 'interactive', 'proposed'];

export const DesignSystem = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('brand');

  // Track scroll position to highlight active section
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200; // Offset for header

      for (const sectionId of NAV_SECTIONS) {
        const element = document.getElementById(sectionId);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const gradients: GradientSwatch[] = [
    {
      name: 'Mint (Airy)',
      css: 'linear-gradient(180deg, #FFFFFF 0%, #FFFFFF 50%, #D5EEEB 100%)',
      style: { background: 'linear-gradient(180deg, #FFFFFF 0%, #FFFFFF 50%, #D5EEEB 100%)' }
    },
    {
      name: 'Coral (Airy)',
      css: 'linear-gradient(180deg, #FFFFFF 0%, #FFFFFF 50%, #FBD3D1 100%)',
      style: { background: 'linear-gradient(180deg, #FFFFFF 0%, #FFFFFF 50%, #FBD3D1 100%)' }
    },
    {
      name: 'Mint (Horizontal)',
      css: 'linear-gradient(90deg, #FFFFFF 0%, #FFFFFF 40%, #D5EEEB 100%)',
      style: { background: 'linear-gradient(90deg, #FFFFFF 0%, #FFFFFF 40%, #D5EEEB 100%)' }
    },
    {
      name: 'Coral (Horizontal)',
      css: 'linear-gradient(90deg, #FFFFFF 0%, #FFFFFF 40%, #FBD3D1 100%)',
      style: { background: 'linear-gradient(90deg, #FFFFFF 0%, #FFFFFF 40%, #FBD3D1 100%)' }
    }
  ];

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    showToast(`Copied ${label}`, 'success');
  };

  // Icons to showcase (valid HappyIcons from happyicon.css)
  const showcaseIcons = [
    'dashboard', 'calendar', 'bell', 'tools', 'user', 'mail-open',
    'heart', 'star', 'search', 'filter', 'plus-circle', 'pencil',
    'recycle-bin', 'download', 'lock', 'link', 'image', 'file',
    'graph-bar', 'tick-circle', 'share', 'home', 'offcanvas-menu', 'cross'
  ];

  return (
    <TooltipProvider>
    <div className="min-h-screen bg-[#F6F8F5]">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <h1 className="font-wagner text-3xl tracking-tight">Ägile Ässets</h1>
          <p className="text-sm text-gray-600">Design System</p>
        </div>
      </header>

      {/* Sticky Section Nav */}
      <nav className="bg-white border-b border-gray-200 sticky top-[72px] z-40">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex gap-3 overflow-x-auto text-sm pb-1">
            {[
              { id: 'colors', label: 'Colors' },
              { id: 'gradients', label: 'Gradients' },
              { id: 'typography', label: 'Typography' },
              { id: 'buttons', label: 'Buttons' },
              { id: 'forms', label: 'Forms' },
              { id: 'cards', label: 'Cards' },
              { id: 'badges', label: 'Badges' },
              { id: 'icons', label: 'Icons' },
              { id: 'platforms', label: 'Platforms' },
              { id: 'status', label: 'Status' },
              { id: 'avatars', label: 'Avatars' },
              { id: 'media', label: 'Media' },
              { id: 'tables', label: 'Tables' },
              { id: 'tabs', label: 'Tabs' },
              { id: 'tags', label: 'Tags' },
              { id: 'upload', label: 'Upload' },
              { id: 'interactive', label: 'Interactive' },
            ].map(({ id, label }) => (
              <a
                key={id}
                href={`#${id}`}
                className={`whitespace-nowrap px-3 py-1 rounded-full transition-colors ${
                  activeSection === id
                    ? 'bg-black text-white font-medium'
                    : 'text-gray-600 hover:text-black hover:bg-gray-100'
                }`}
              >
                {label}
              </a>
            ))}
            <a
              href="#proposed"
              className={`whitespace-nowrap px-3 py-1 rounded-full font-medium transition-colors ${
                activeSection === 'proposed'
                  ? 'bg-[#D0FF00] text-black'
                  : 'text-gray-600 hover:text-black hover:bg-gray-100 border border-dashed border-gray-400'
              }`}
            >
              Proposed
            </a>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12 space-y-20">

        {/* Hero */}
        <section
          className="rounded-2xl p-16 text-center"
          style={{ background: 'linear-gradient(180deg, #FFFFFF 0%, #FFFFFF 50%, #D5EEEB 100%)' }}
        >
          <h1 className="font-wagner text-6xl tracking-tight mb-4">Design System</h1>
          <p className="text-xl text-gray-700">Ägile Ässets Brand Guidelines</p>
        </section>

        {/* Brand Identity */}
        <section id="brand">
          <h2 className="font-wagner text-4xl tracking-tight mb-8 text-black">Brand Identity</h2>

          {/* Logos */}
          <h3 className="text-xl font-semibold mb-4">Logos</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {/* Main Logo - Light Background */}
            <div className="bg-white rounded-xl p-8 shadow-md flex flex-col items-center">
              <img src="/images/logo.png" alt="Ägile Ässets Logo" className="h-12 object-contain mb-4" />
              <p className="text-sm font-medium text-gray-700">Main Logo</p>
              <p className="text-xs text-gray-500">Light backgrounds</p>
            </div>

            {/* Simple Logo - Light Background */}
            <div className="bg-white rounded-xl p-8 shadow-md flex flex-col items-center">
              <img src="/images/aa-logo-simple.png" alt="Ägile Ässets Simple Logo" className="h-12 object-contain mb-4" />
              <p className="text-sm font-medium text-gray-700">Simple Logo</p>
              <p className="text-xs text-gray-500">Nav & compact spaces</p>
            </div>

            {/* Main Logo - Dark Background */}
            <div className="bg-[#074F47] rounded-xl p-8 shadow-md flex flex-col items-center">
              <img src="/images/logo.png" alt="Ägile Ässets Logo" className="h-12 object-contain mb-4 brightness-0 invert" />
              <p className="text-sm font-medium text-white">Main Logo</p>
              <p className="text-xs text-white/70">Dark backgrounds</p>
            </div>

            {/* Simple Logo - Dark Background */}
            <div className="bg-black rounded-xl p-8 shadow-md flex flex-col items-center">
              <img src="/images/aa-logo-simple.png" alt="Ägile Ässets Simple Logo" className="h-12 object-contain mb-4 brightness-0 invert" />
              <p className="text-sm font-medium text-white">Simple Logo</p>
              <p className="text-xs text-white/70">Dark backgrounds</p>
            </div>
          </div>

          {/* Brand Values */}
          <h3 className="text-xl font-semibold mb-4">Brand Values</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-6 text-center shadow-md">
              <div className="text-4xl mb-3">⚡</div>
              <h3 className="font-wagner text-xl mb-2">Agile</h3>
              <p className="text-sm text-gray-600">Fast, flexible, adaptive</p>
            </div>
            <div className="bg-white rounded-xl p-6 text-center shadow-md">
              <div className="text-4xl mb-3">🎨</div>
              <h3 className="font-wagner text-xl mb-2">Creative</h3>
              <p className="text-sm text-gray-600">Bold, innovative, expressive</p>
            </div>
            <div className="bg-white rounded-xl p-6 text-center shadow-md">
              <div className="text-4xl mb-3">🌍</div>
              <h3 className="font-wagner text-xl mb-2">Cosmopolitan</h3>
              <p className="text-sm text-gray-600">Global, diverse, connected</p>
            </div>
          </div>
        </section>

        {/* Color Palette */}
        <section id="colors">
          <h2 className="font-wagner text-4xl tracking-tight mb-4 text-black">Color Palette</h2>
          <p className="text-gray-600 mb-8">
            Full shade reference extracted from <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">tailwind.config.js</code>.
            Click any swatch to copy its Tailwind class.
          </p>

          {/* Whitelabel note */}
          <div className="mb-10 bg-soft-mint border border-light-teal-gray rounded-xl p-5">
            <div className="flex gap-3 items-start">
              <Icon name="info" size="md" className="text-dark-teal flex-shrink-0 mt-0.5" ariaHidden />
              <div>
                <p className="text-sm font-semibold text-dark-teal mb-1">Whitelabel runtime theming</p>
                <p className="text-sm text-dark-teal/80 mb-3">
                  <strong>Primary</strong> and <strong>Secondary</strong> families respond to runtime CSS-variable overrides
                  injected by <code className="text-xs bg-white/60 px-1 rounded">ThemeContext</code> (via{' '}
                  <code className="text-xs bg-white/60 px-1 rounded">generateColorPalette()</code>). All other color families
                  are brand-locked and will not change per Brand.
                </p>
                <div className="flex flex-wrap gap-3 text-xs">
                  <span className="bg-white/70 border border-primary-300 text-dark-teal px-2 py-1 rounded-full font-medium">
                    primary-* — whitelabel-safe
                  </span>
                  <span className="bg-white/70 border border-primary-300 text-dark-teal px-2 py-1 rounded-full font-medium">
                    secondary-* — whitelabel-safe
                  </span>
                  <span className="bg-white/70 border border-gray-300 text-gray-700 px-2 py-1 rounded-full">
                    coral-* — brand-locked
                  </span>
                  <span className="bg-white/70 border border-gray-300 text-gray-700 px-2 py-1 rounded-full">
                    accent colors — brand-locked
                  </span>
                  <span className="bg-white/70 border border-gray-300 text-gray-700 px-2 py-1 rounded-full">
                    status colors — brand-locked
                  </span>
                  <span className="bg-white/70 border border-gray-300 text-gray-700 px-2 py-1 rounded-full">
                    gray-* — brand-locked
                  </span>
                </div>
              </div>
            </div>
          </div>

          <ColorPaletteReference copyToClipboard={copyToClipboard} />

          {/* Usage guidance table */}
          <div className="mt-10 bg-white rounded-xl shadow-md overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold">Semantic Usage Conventions</h3>
              <p className="text-sm text-gray-500 mt-0.5">When to reach for which color</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-6 py-3 font-medium text-gray-700">Context</th>
                    <th className="text-left px-6 py-3 font-medium text-gray-700">Recommended</th>
                    <th className="text-left px-6 py-3 font-medium text-gray-700">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {USAGE_CONVENTIONS.map((row, i) => (
                    <tr key={i} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-3 font-medium text-gray-800">{row.context}</td>
                      <td className="px-6 py-3">
                        <div className="flex flex-wrap gap-1.5">
                          {row.classes.map(cls => (
                            <code key={cls} className="text-xs bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded font-mono">
                              {cls}
                            </code>
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-3 text-gray-500 text-xs">{row.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-6 py-4 bg-amber-50 border-t border-amber-100">
              <p className="text-xs text-amber-800">
                <strong>Warning:</strong> Avoid raw Tailwind colors like{' '}
                <code className="bg-amber-100 px-1 rounded">blue-*</code>,{' '}
                <code className="bg-amber-100 px-1 rounded">purple-*</code>,{' '}
                <code className="bg-amber-100 px-1 rounded">indigo-*</code> — they bypass the
                whitelabel theme system and will not adapt to Brand branding.
              </p>
            </div>
          </div>
        </section>

        {/* Gradients */}
        <section id="gradients">
          <h2 className="font-wagner text-4xl tracking-tight mb-8 text-black">Gradients</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {gradients.map(gradient => (
              <div key={gradient.name} className="bg-white rounded-xl p-6 shadow-md">
                <div
                  className="w-full h-40 rounded-lg mb-4"
                  style={gradient.style}
                />
                <h3 className="font-semibold mb-2">{gradient.name}</h3>
                <button
                  onClick={() => copyToClipboard(gradient.css, 'CSS')}
                  className="w-full bg-gray-100 rounded px-3 py-2 text-xs font-mono text-left hover:bg-gray-200 transition-colors break-all"
                >
                  {gradient.css}
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* Typography */}
        <section id="typography">
          <h2 className="font-wagner text-4xl tracking-tight mb-8 text-black">Typography</h2>

          {/* Wagner Font */}
          <div className="mb-12 bg-white rounded-xl p-8 shadow-md">
            <h3 className="text-xl font-semibold mb-6">Wagner - Headers</h3>
            <p className="text-sm text-gray-500 mb-6">Letter spacing: -0.05em (Canva: -50)</p>
            <div className="space-y-4">
              <div>
                <p className="text-xs text-gray-500 mb-1">H1 - 3.75rem (60px)</p>
                <h1 className="font-wagner text-6xl text-black" style={{ letterSpacing: '-0.05em' }}>The quick brown fox</h1>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">H2 - 3rem (48px)</p>
                <h2 className="font-wagner text-5xl text-black" style={{ letterSpacing: '-0.05em' }}>The quick brown fox</h2>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">H3 - 2.25rem (36px)</p>
                <h3 className="font-wagner text-4xl text-black" style={{ letterSpacing: '-0.05em' }}>The quick brown fox</h3>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">H4 - 1.875rem (30px)</p>
                <h4 className="font-wagner text-3xl text-black" style={{ letterSpacing: '-0.05em' }}>The quick brown fox</h4>
              </div>
            </div>
          </div>

          {/* DM Sans Font */}
          <div className="bg-white rounded-xl p-8 shadow-md">
            <h3 className="text-xl font-semibold mb-6">DM Sans - Body</h3>
            <div className="space-y-4">
              <div>
                <p className="text-xs text-gray-500 mb-1">Body - 1rem (16px)</p>
                <p className="text-base text-black">The quick brown fox jumps over the lazy dog</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Small - 0.875rem (14px)</p>
                <p className="text-sm text-black">The quick brown fox jumps over the lazy dog</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Extra Small - 0.75rem (12px)</p>
                <p className="text-xs text-black">The quick brown fox jumps over the lazy dog</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Bold</p>
                <p className="text-base font-bold text-black">The quick brown fox jumps over the lazy dog</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Italic</p>
                <p className="text-base italic text-black">The quick brown fox jumps over the lazy dog</p>
              </div>
            </div>
          </div>
        </section>

        {/* Buttons */}
        <section id="buttons">
          <h2 className="font-wagner text-4xl tracking-tight mb-8 text-black">Buttons</h2>

          {/* Current Component Variants */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold mb-4">Current Component Variants</h3>
            <div className="flex flex-wrap gap-4 bg-white rounded-xl p-8 shadow-md">
              <Button variant="primary">Primary (Coral)</Button>
              <Button variant="secondary">Secondary (Teal)</Button>
              <Button variant="danger">Danger</Button>
            </div>
          </div>

          {/* Solid Buttons - Brand Colors */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold mb-4">Solid Buttons - Brand Colors</h3>
            <div className="flex flex-wrap gap-4 bg-white rounded-xl p-8 shadow-md">
              <button className="px-6 py-2.5 bg-black text-white rounded-lg font-semibold text-sm border border-transparent hover:bg-white hover:text-black hover:border-black focus:bg-black focus:text-white focus:border-[#D0FF00] focus:outline-none transition-all">
                Black
              </button>
              <button className="px-6 py-2.5 bg-[#FF6565] text-white rounded-lg font-semibold text-sm hover:bg-[#e55555] transition-all">
                Coral
              </button>
              <button className="px-6 py-2.5 bg-[#69C7C1] text-white rounded-lg font-semibold text-sm hover:bg-[#5ab5af] transition-all">
                Teal
              </button>
              <button className="px-6 py-2.5 bg-[#D0FF00] text-black rounded-lg font-semibold text-sm hover:bg-[#bce600] transition-all">
                Lime
              </button>
              <button className="px-6 py-2.5 bg-[#074F47] text-white rounded-lg font-semibold text-sm hover:bg-[#063d37] transition-all">
                Dark Teal
              </button>
            </div>
          </div>

          {/* Outlined Buttons */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold mb-4">Outlined Buttons</h3>
            <div className="flex flex-wrap gap-4 bg-white rounded-xl p-8 shadow-md">
              <button className="px-6 py-2.5 bg-transparent border-2 border-black text-black rounded-lg font-semibold text-sm hover:bg-black hover:text-white transition-all">
                Black Outline
              </button>
              <button className="px-6 py-2.5 bg-transparent border-2 border-[#FF6565] text-[#FF6565] rounded-lg font-semibold text-sm hover:bg-[#FF6565] hover:text-white transition-all">
                Coral Outline
              </button>
              <button className="px-6 py-2.5 bg-transparent border-2 border-[#69C7C1] text-[#69C7C1] rounded-lg font-semibold text-sm hover:bg-[#69C7C1] hover:text-white transition-all">
                Teal Outline
              </button>
              <button className="px-6 py-2.5 bg-transparent border-2 border-[#074F47] text-[#074F47] rounded-lg font-semibold text-sm hover:bg-[#074F47] hover:text-white transition-all">
                Dark Teal Outline
              </button>
            </div>
          </div>

          {/* Ghost Buttons */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold mb-4">Ghost Buttons</h3>
            <div className="flex flex-wrap gap-4 bg-white rounded-xl p-8 shadow-md">
              <button className="px-6 py-2.5 bg-transparent text-black rounded-lg font-semibold text-sm hover:bg-gray-100 transition-all">
                Ghost Black
              </button>
              <button className="px-6 py-2.5 bg-transparent text-[#FF6565] rounded-lg font-semibold text-sm hover:bg-[#FF6565]/10 transition-all">
                Ghost Coral
              </button>
              <button className="px-6 py-2.5 bg-transparent text-[#69C7C1] rounded-lg font-semibold text-sm hover:bg-[#69C7C1]/10 transition-all">
                Ghost Teal
              </button>
              <button className="px-6 py-2.5 bg-transparent text-gray-600 rounded-lg font-semibold text-sm hover:bg-gray-100 underline transition-all">
                Link Style
              </button>
            </div>
          </div>

          {/* Buttons on Dark Background */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold mb-4">On Dark Backgrounds</h3>
            <div className="flex flex-wrap gap-4 bg-[#074F47] rounded-xl p-8 shadow-md">
              <button className="px-6 py-2.5 bg-white text-[#074F47] rounded-lg font-semibold text-sm hover:bg-gray-100 transition-all">
                White Solid
              </button>
              <button className="px-6 py-2.5 bg-transparent border-2 border-white text-white rounded-lg font-semibold text-sm hover:bg-white hover:text-[#074F47] transition-all">
                White Outline
              </button>
              <button className="px-6 py-2.5 bg-[#D0FF00] text-black rounded-lg font-semibold text-sm hover:bg-[#bce600] transition-all">
                Lime Accent
              </button>
              <button className="px-6 py-2.5 bg-[#FF6565] text-white rounded-lg font-semibold text-sm hover:bg-[#e55555] transition-all">
                Coral
              </button>
            </div>
            <div className="flex flex-wrap gap-4 bg-black rounded-xl p-8 shadow-md mt-4">
              <button className="px-6 py-2.5 bg-white text-black rounded-lg font-semibold text-sm hover:bg-gray-100 transition-all">
                White Solid
              </button>
              <button className="px-6 py-2.5 bg-transparent border-2 border-white text-white rounded-lg font-semibold text-sm hover:bg-white hover:text-black transition-all">
                White Outline
              </button>
              <button className="px-6 py-2.5 bg-[#D0FF00] text-black rounded-lg font-semibold text-sm hover:bg-[#bce600] transition-all">
                Lime Accent
              </button>
              <button className="px-6 py-2.5 bg-[#69C7C1] text-white rounded-lg font-semibold text-sm hover:bg-[#5ab5af] transition-all">
                Teal
              </button>
            </div>
          </div>

          {/* Pill Buttons */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold mb-4">Pill Buttons</h3>
            <div className="flex flex-wrap gap-4 bg-white rounded-xl p-8 shadow-md">
              <button className="px-6 py-2.5 bg-black text-white rounded-full font-semibold text-sm border border-transparent hover:bg-white hover:text-black hover:border-black focus:bg-black focus:text-white focus:border-[#D0FF00] focus:outline-none transition-all">
                Black Pill
              </button>
              <button className="px-6 py-2.5 bg-[#FF6565] text-white rounded-full font-semibold text-sm hover:bg-[#e55555] transition-all">
                Coral Pill
              </button>
              <button className="px-6 py-2.5 bg-transparent border-2 border-black text-black rounded-full font-semibold text-sm hover:bg-black hover:text-white transition-all">
                Outline Pill
              </button>
              <button className="px-6 py-2.5 bg-[#D0FF00] text-black rounded-full font-semibold text-sm hover:bg-[#bce600] transition-all">
                Lime Pill
              </button>
            </div>
          </div>

          {/* Buttons with Icons */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold mb-4">With Icons</h3>
            <div className="flex flex-wrap gap-4 bg-white rounded-xl p-8 shadow-md">
              <button className="px-6 py-2.5 bg-black text-white rounded-lg font-semibold text-sm border border-transparent hover:bg-white hover:text-black hover:border-black focus:bg-black focus:text-white focus:border-[#D0FF00] focus:outline-none transition-all inline-flex items-center gap-2">
                <Icon name="plus-circle" size="sm" ariaHidden />
                Add New
              </button>
              <button className="px-6 py-2.5 bg-[#FF6565] text-white rounded-lg font-semibold text-sm hover:bg-[#e55555] transition-all inline-flex items-center gap-2">
                <Icon name="download" size="sm" ariaHidden />
                Download
              </button>
              <button className="px-6 py-2.5 bg-transparent border-2 border-black text-black rounded-lg font-semibold text-sm hover:bg-black hover:text-white transition-all inline-flex items-center gap-2">
                <Icon name="share" size="sm" ariaHidden />
                Share
              </button>
              <button className="px-6 py-2.5 bg-[#69C7C1] text-white rounded-lg font-semibold text-sm hover:bg-[#5ab5af] transition-all inline-flex items-center gap-2">
                Save
                <Icon name="tick-circle" size="sm" ariaHidden />
              </button>
            </div>
          </div>

          {/* Icon-Only Buttons */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold mb-4">Icon-Only Buttons</h3>
            <div className="flex flex-wrap items-center gap-4 bg-white rounded-xl p-8 shadow-md">
              <button className="w-10 h-10 bg-black text-white rounded-lg flex items-center justify-center border border-transparent hover:bg-white hover:text-black hover:border-black focus:bg-black focus:text-white focus:border-[#D0FF00] focus:outline-none transition-all">
                <Icon name="plus-circle" size="sm" ariaHidden />
              </button>
              <button className="w-10 h-10 bg-[#FF6565] text-white rounded-lg flex items-center justify-center hover:bg-[#e55555] transition-all">
                <Icon name="heart" size="sm" className="text-[18px] drop-shadow-[0_0_1px_rgba(255,255,255,0.8)]" ariaHidden />
              </button>
              <button className="w-10 h-10 bg-[#FF6565] text-white rounded-lg flex items-center justify-center hover:bg-[#e55555] transition-all">
                <span className="text-lg">🤍</span>
              </button>
              <button className="w-10 h-10 bg-transparent border border-gray-300 text-black rounded-lg flex items-center justify-center hover:bg-gray-100 transition-all">
                <Icon name="pencil" size="sm" className="text-[18px]" ariaHidden />
              </button>
              <button className="w-10 h-10 bg-transparent border border-gray-300 text-black rounded-lg flex items-center justify-center hover:bg-gray-100 transition-all">
                <span className="text-lg">✏️</span>
              </button>
              {/* Proposed: Filled icon using FontAwesome */}
              <div className="relative">
                <button className="w-10 h-10 bg-transparent border border-gray-300 text-black rounded-lg flex items-center justify-center hover:bg-gray-100 transition-all">
                  <svg className="w-[18px] h-[18px]" fill="currentColor" viewBox="0 0 512 512">
                    <path d="M362.7 19.3L314.3 67.7 444.3 197.7l48.4-48.4c25-25 25-65.5 0-90.5L453.3 19.3c-25-25-65.5-25-90.5 0zm-71 71L58.6 323.5c-10.4 10.4-18 23.3-22.2 37.4L1 481.2C-1.5 489.7 .8 498.8 7 505s15.3 8.5 23.7 6.1l120.3-35.4c14.1-4.2 27-11.8 37.4-22.2L421.7 220.3 291.7 90.3z"/>
                  </svg>
                </button>
                <span className="absolute -top-2 -right-2 bg-[#D0FF00] text-black text-[9px] font-bold px-1.5 py-0.5 rounded">FILLED</span>
              </div>
              <button className="w-10 h-10 bg-transparent border-2 border-gray-300 text-gray-600 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-all">
                <Icon name="recycle-bin" size="sm" ariaHidden />
              </button>
              <button className="w-10 h-10 bg-black text-white rounded-full flex items-center justify-center border border-transparent hover:bg-white hover:text-black hover:border-black focus:bg-black focus:text-white focus:border-[#D0FF00] focus:outline-none transition-all">
                <Icon name="plus-circle" size="sm" ariaHidden />
              </button>
              <button className="w-10 h-10 bg-[#69C7C1] text-white rounded-full flex items-center justify-center hover:bg-[#5ab5af] transition-all">
                <Icon name="share" size="sm" ariaHidden />
              </button>
            </div>
          </div>

          {/* Sizes */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold mb-4">Sizes</h3>
            <div className="flex flex-wrap items-center gap-4 bg-white rounded-xl p-8 shadow-md">
              <button className="px-4 py-1.5 bg-black text-white rounded-lg font-semibold text-xs border border-transparent hover:bg-white hover:text-black hover:border-black focus:bg-black focus:text-white focus:border-[#D0FF00] focus:outline-none transition-all">
                Extra Small
              </button>
              <button className="px-4 py-2 bg-black text-white rounded-lg font-semibold text-sm border border-transparent hover:bg-white hover:text-black hover:border-black focus:bg-black focus:text-white focus:border-[#D0FF00] focus:outline-none transition-all">
                Small
              </button>
              <button className="px-6 py-2.5 bg-black text-white rounded-lg font-semibold text-sm border border-transparent hover:bg-white hover:text-black hover:border-black focus:bg-black focus:text-white focus:border-[#D0FF00] focus:outline-none transition-all">
                Medium
              </button>
              <button className="px-8 py-3 bg-black text-white rounded-lg font-semibold text-base border border-transparent hover:bg-white hover:text-black hover:border-black focus:bg-black focus:text-white focus:border-[#D0FF00] focus:outline-none transition-all">
                Large
              </button>
              <button className="px-10 py-4 bg-black text-white rounded-lg font-semibold text-lg border border-transparent hover:bg-white hover:text-black hover:border-black focus:bg-black focus:text-white focus:border-[#D0FF00] focus:outline-none transition-all">
                Extra Large
              </button>
            </div>
          </div>

          {/* States */}
          <div>
            <h3 className="text-xl font-semibold mb-4">States</h3>
            <div className="flex flex-wrap gap-4 bg-white rounded-xl p-8 shadow-md">
              <button className="px-6 py-2.5 bg-black text-white border border-transparent rounded-lg font-semibold text-sm transition-all">
                Default
              </button>
              <button className="px-6 py-2.5 bg-white text-black border border-black rounded-lg font-semibold text-sm transition-all">
                Hover
              </button>
              <button className="px-6 py-2.5 bg-white text-black border border-[#D0FF00] rounded-lg font-semibold text-sm transition-all">
                Focused
              </button>
              <button className="px-6 py-2.5 bg-gray-300 text-gray-500 border border-transparent rounded-lg font-semibold text-sm cursor-not-allowed">
                Disabled
              </button>
              <button className="px-6 py-2.5 bg-black text-white border border-transparent rounded-lg font-semibold text-sm transition-all inline-flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Loading
              </button>
            </div>
          </div>
        </section>

        {/* Form Elements */}
        <section id="forms">
          <h2 className="font-wagner text-4xl tracking-tight mb-8 text-black">Form Elements</h2>

          <div className="bg-white rounded-xl p-8 shadow-md space-y-8">
            {/* Input */}
            <div>
              <h3 className="text-lg font-semibold mb-2">Input</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input placeholder="Default input" />
                <Input placeholder="With label" label="Email" />
                <Input placeholder="With error" error="This field is required" />
                <Input placeholder="Disabled" disabled />
              </div>
            </div>

            {/* Textarea */}
            <div>
              <h3 className="text-lg font-semibold mb-2">Textarea</h3>
              <Textarea placeholder="Enter your message..." rows={4} />
            </div>

            {/* Select */}
            <div>
              <h3 className="text-lg font-semibold mb-2">Select</h3>
              <Select
                placeholder="Choose an option"
                options={[
                  { value: '1', label: 'Option 1' },
                  { value: '2', label: 'Option 2' },
                  { value: '3', label: 'Option 3' },
                ]}
              />
            </div>

            {/* Checkbox */}
            <div>
              <h3 className="text-lg font-semibold mb-2">Checkbox</h3>
              <div className="space-y-3">
                <Checkbox label="Unchecked" />
                <Checkbox label="Checked" checked />
                <Checkbox label="Disabled" disabled />
              </div>
            </div>

            {/* Radio */}
            <div>
              <h3 className="text-lg font-semibold mb-2">Radio</h3>
              <RadioGroup defaultValue="1">
                <RadioItem value="1" label="Option 1" />
                <RadioItem value="2" label="Option 2" />
                <RadioItem value="3" label="Option 3" />
              </RadioGroup>
            </div>

            {/* Toggle */}
            <div>
              <h3 className="text-lg font-semibold mb-2">Toggle</h3>
              <div className="space-y-3">
                <Toggle label="Toggle me" />
                <Toggle label="Default on" checked />
              </div>
            </div>
          </div>
        </section>

        {/* Cards */}
        <section id="cards">
          <h2 className="font-wagner text-4xl tracking-tight mb-8 text-black">Cards</h2>

          {/* Shadow Levels */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold mb-4">Shadow Levels</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <p className="text-sm font-semibold mb-1">Small</p>
                <p className="text-xs text-gray-500">shadow-sm</p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-md">
                <p className="text-sm font-semibold mb-1">Medium</p>
                <p className="text-xs text-gray-500">shadow-md</p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <p className="text-sm font-semibold mb-1">Large</p>
                <p className="text-xs text-gray-500">shadow-lg</p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-xl">
                <p className="text-sm font-semibold mb-1">Extra Large</p>
                <p className="text-xs text-gray-500">shadow-xl</p>
              </div>
            </div>
          </div>

          {/* Border Radius */}
          <div>
            <h3 className="text-xl font-semibold mb-4">Border Radius</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg p-6 shadow-md">
                <p className="text-sm font-semibold mb-1">Large</p>
                <p className="text-xs text-gray-500">rounded-lg (0.5rem)</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-md">
                <p className="text-sm font-semibold mb-1">Extra Large</p>
                <p className="text-xs text-gray-500">rounded-xl (0.75rem)</p>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-md">
                <p className="text-sm font-semibold mb-1">2X Large</p>
                <p className="text-xs text-gray-500">rounded-2xl (1rem)</p>
              </div>
            </div>
          </div>
        </section>

        {/* Badges */}
        <section id="badges">
          <h2 className="font-wagner text-4xl tracking-tight mb-8 text-black">Badges</h2>
          <div className="bg-white rounded-xl p-8 shadow-md">
            <div className="flex flex-wrap gap-4">
              <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">
                Success
              </span>
              <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-sm font-medium rounded-full">
                Warning
              </span>
              <span className="px-3 py-1 bg-red-100 text-red-800 text-sm font-medium rounded-full">
                Danger
              </span>
              <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                Info
              </span>
              <span className="px-3 py-1 bg-gray-100 text-black text-sm font-medium rounded-full">
                Neutral
              </span>
            </div>
          </div>
        </section>

        {/* Icons */}
        <section id="icons">
          <h2 className="font-wagner text-4xl tracking-tight mb-8 text-black">Icons</h2>
          <div className="bg-white rounded-xl p-8 shadow-md">
            <p className="text-sm text-gray-600 mb-6">HappyIcons font - Common icons</p>
            <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-6">
              {showcaseIcons.map(iconName => (
                <div key={iconName} className="flex flex-col items-center gap-2 p-3 hover:bg-gray-50 rounded-lg transition-colors">
                  <Icon name={iconName} size="lg" />
                  <p className="text-xs text-gray-600 text-center">{iconName}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Social Platform Colors */}
        <section id="platforms">
          <h2 className="font-wagner text-4xl tracking-tight mb-8 text-black">Social Platform Colors</h2>
          <p className="text-gray-600 mb-6">Brand colors for each social media platform. Used for channel badges, icons, and indicators.</p>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            {[
              { name: 'LinkedIn', color: '#0A66C2', icon: faLinkedin },
              { name: 'Facebook', color: '#1877F2', icon: faFacebook },
              { name: 'Instagram', color: '#E4405F', icon: faInstagram },
              { name: 'X (Twitter)', color: '#000000', icon: faXTwitter },
              { name: 'YouTube', color: '#FF0000', icon: faYoutube },
              { name: 'TikTok', color: '#000000', icon: faTiktok },
              { name: 'Pinterest', color: '#E60023', icon: faPinterest },
              { name: 'Threads', color: '#000000', icon: faThreads },
              { name: 'Google Business', color: '#4285F4', icon: faGoogle },
              { name: 'Bluesky', color: '#0085FF', icon: faBluesky },
            ].map((platform) => (
              <button
                key={platform.name}
                onClick={() => copyToClipboard(platform.color, platform.color)}
                className="bg-white rounded-xl p-4 shadow-md hover:shadow-lg transition-shadow cursor-pointer"
              >
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center text-white mx-auto mb-3"
                  style={{ backgroundColor: platform.color }}
                >
                  <FontAwesomeIcon icon={platform.icon} className="text-xl" />
                </div>
                <p className="font-medium text-sm text-center">{platform.name}</p>
                <p className="text-xs text-gray-500 text-center font-mono">{platform.color}</p>
              </button>
            ))}
          </div>

          {/* Platform Badges */}
          <h3 className="text-xl font-semibold mb-4">Platform Badges (Usage Examples)</h3>
          <div className="bg-white rounded-xl p-6 shadow-md mb-6">
            <div className="flex flex-wrap gap-3">
              {[
                { name: 'LinkedIn', color: '#0A66C2', icon: faLinkedin },
                { name: 'Facebook', color: '#1877F2', icon: faFacebook },
                { name: 'Instagram', color: '#E4405F', icon: faInstagram },
                { name: 'X', color: '#000000', icon: faXTwitter },
                { name: 'YouTube', color: '#FF0000', icon: faYoutube },
                { name: 'TikTok', color: '#000000', icon: faTiktok },
              ].map((p) => (
                <span
                  key={p.name}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-white text-sm font-medium"
                  style={{ backgroundColor: p.color }}
                >
                  <FontAwesomeIcon icon={p.icon} className="text-xs" />
                  {p.name}
                </span>
              ))}
            </div>
          </div>

          {/* Large Icons (No Background) */}
          <h3 className="text-xl font-semibold mb-4">Platform Icons (Large, No Background)</h3>
          <div className="bg-white rounded-xl p-6 shadow-md mb-6">
            <div className="flex flex-wrap gap-8 items-center">
              {[
                { name: 'LinkedIn', color: '#0A66C2', icon: faLinkedin },
                { name: 'Facebook', color: '#1877F2', icon: faFacebook },
                { name: 'Instagram', color: '#E4405F', icon: faInstagram },
                { name: 'X (Twitter)', color: '#000000', icon: faXTwitter },
                { name: 'YouTube', color: '#FF0000', icon: faYoutube },
                { name: 'TikTok', color: '#000000', icon: faTiktok },
                { name: 'Pinterest', color: '#E60023', icon: faPinterest },
                { name: 'Threads', color: '#000000', icon: faThreads },
                { name: 'Google', color: '#4285F4', icon: faGoogle },
                { name: 'Bluesky', color: '#0085FF', icon: faBluesky },
              ].map((p) => (
                <div key={p.name} className="text-center">
                  <FontAwesomeIcon
                    icon={p.icon}
                    className="text-4xl mb-2"
                    style={{ color: p.color }}
                  />
                  <p className="text-xs text-gray-500">{p.name}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Small Icon Pills */}
          <h3 className="text-xl font-semibold mb-4">Compact Pills (Channel Indicators)</h3>
          <div className="bg-white rounded-xl p-6 shadow-md">
            <div className="flex flex-wrap gap-2">
              {[
                { color: '#0A66C2', icon: faLinkedin },
                { color: '#1877F2', icon: faFacebook },
                { color: '#E4405F', icon: faInstagram },
                { color: '#000000', icon: faXTwitter },
                { color: '#FF0000', icon: faYoutube },
                { color: '#000000', icon: faTiktok },
                { color: '#E60023', icon: faPinterest },
                { color: '#000000', icon: faThreads },
                { color: '#4285F4', icon: faGoogle },
                { color: '#0085FF', icon: faBluesky },
              ].map((p, i) => (
                <span
                  key={i}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white"
                  style={{ backgroundColor: p.color }}
                >
                  <FontAwesomeIcon icon={p.icon} className="text-sm" />
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Post Status System */}
        <section id="status">
          <h2 className="font-wagner text-4xl tracking-tight mb-8 text-black">Post Status System</h2>
          <p className="text-gray-600 mb-6">Consistent status indicators used across the app for post lifecycle.</p>

          {/* Full Status Badges */}
          <h3 className="text-xl font-semibold mb-4">Status Badges</h3>
          <div className="bg-white rounded-xl p-6 shadow-md mb-6">
            <div className="flex flex-wrap gap-3">
              {[
                { status: 'Draft', bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-200' },
                { status: 'Pending Review', bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200' },
                { status: 'Changes Requested', bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
                { status: 'Approved', bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
                { status: 'Scheduled', bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
                { status: 'Publishing', bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200' },
                { status: 'Published', bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
                { status: 'Failed', bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
                { status: 'Cancelled', bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-200' },
              ].map((s) => (
                <span
                  key={s.status}
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium border ${s.bg} ${s.text} ${s.border}`}
                >
                  <Icon name={s.status === 'Failed' ? 'cross-circle' : s.status === 'Published' || s.status === 'Approved' ? 'tick-circle' : s.status === 'Scheduled' ? 'calendar' : 'pencil'} size="xs" ariaHidden />
                  {s.status}
                </span>
              ))}
            </div>
          </div>

          {/* Status Dots */}
          <h3 className="text-xl font-semibold mb-4">Status Dots (Compact)</h3>
          <div className="bg-white rounded-xl p-6 shadow-md">
            <div className="flex flex-wrap gap-6">
              {[
                { status: 'Draft', color: 'bg-gray-400' },
                { status: 'Pending', color: 'bg-yellow-500' },
                { status: 'Approved', color: 'bg-green-500' },
                { status: 'Scheduled', color: 'bg-purple-500' },
                { status: 'Published', color: 'bg-green-500' },
                { status: 'Failed', color: 'bg-red-500' },
              ].map((s) => (
                <span key={s.status} className="inline-flex items-center gap-2 text-sm">
                  <span className={`w-2 h-2 rounded-full ${s.color}`}></span>
                  {s.status}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Avatars */}
        <section id="avatars">
          <h2 className="font-wagner text-4xl tracking-tight mb-8 text-black">Avatars</h2>
          <p className="text-gray-600 mb-6">User representation patterns for profiles, comments, and team displays.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Sizes */}
            <div className="bg-white rounded-xl p-6 shadow-md">
              <h3 className="text-lg font-semibold mb-4">Sizes</h3>
              <div className="flex items-end gap-4">
                <div className="text-center">
                  <div className="w-8 h-8 rounded-full bg-[#FF6565] text-white flex items-center justify-center font-semibold text-xs">JD</div>
                  <p className="text-xs text-gray-500 mt-2">XS (32px)</p>
                </div>
                <div className="text-center">
                  <div className="w-10 h-10 rounded-full bg-[#69C7C1] text-white flex items-center justify-center font-semibold text-sm">JD</div>
                  <p className="text-xs text-gray-500 mt-2">SM (40px)</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-[#0A66C2] text-white flex items-center justify-center font-semibold">JD</div>
                  <p className="text-xs text-gray-500 mt-2">MD (48px)</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-[#074F47] text-white flex items-center justify-center font-bold text-lg">JD</div>
                  <p className="text-xs text-gray-500 mt-2">LG (64px)</p>
                </div>
                <div className="text-center">
                  <div className="w-24 h-24 rounded-full bg-black text-white flex items-center justify-center font-bold text-2xl">JD</div>
                  <p className="text-xs text-gray-500 mt-2">XL (96px)</p>
                </div>
              </div>
            </div>

            {/* With Status */}
            <div className="bg-white rounded-xl p-6 shadow-md">
              <h3 className="text-lg font-semibold mb-4">With Status Indicator</h3>
              <div className="flex items-center gap-6">
                <div className="relative">
                  <div className="w-12 h-12 rounded-full bg-[#FF6565] text-white flex items-center justify-center font-semibold">AB</div>
                  <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></span>
                </div>
                <div className="relative">
                  <div className="w-12 h-12 rounded-full bg-[#69C7C1] text-white flex items-center justify-center font-semibold">CD</div>
                  <span className="absolute bottom-0 right-0 w-3 h-3 bg-gray-400 border-2 border-white rounded-full"></span>
                </div>
                <div className="relative">
                  <div className="w-12 h-12 rounded-full bg-[#0A66C2] text-white flex items-center justify-center font-semibold">EF</div>
                  <span className="absolute bottom-0 right-0 w-3 h-3 bg-yellow-500 border-2 border-white rounded-full"></span>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-3">Online (green) • Offline (gray) • Away (yellow)</p>
            </div>

            {/* Avatar Group */}
            <div className="bg-white rounded-xl p-6 shadow-md">
              <h3 className="text-lg font-semibold mb-4">Avatar Group (Stacked)</h3>
              <div className="flex -space-x-3">
                <div className="w-10 h-10 rounded-full bg-[#FF6565] text-white flex items-center justify-center font-semibold text-sm border-2 border-white">AB</div>
                <div className="w-10 h-10 rounded-full bg-[#69C7C1] text-white flex items-center justify-center font-semibold text-sm border-2 border-white">CD</div>
                <div className="w-10 h-10 rounded-full bg-[#0A66C2] text-white flex items-center justify-center font-semibold text-sm border-2 border-white">EF</div>
                <div className="w-10 h-10 rounded-full bg-[#074F47] text-white flex items-center justify-center font-semibold text-sm border-2 border-white">GH</div>
                <div className="w-10 h-10 rounded-full bg-gray-200 text-gray-600 flex items-center justify-center font-semibold text-xs border-2 border-white">+5</div>
              </div>
            </div>

            {/* With Name */}
            <div className="bg-white rounded-xl p-6 shadow-md">
              <h3 className="text-lg font-semibold mb-4">Avatar + Name</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#FF6565] text-white flex items-center justify-center font-semibold text-sm">JD</div>
                  <div>
                    <p className="font-medium text-black">Jane Doe</p>
                    <p className="text-sm text-gray-500">Team Admin</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#69C7C1] text-white flex items-center justify-center font-semibold text-sm">JS</div>
                  <div>
                    <p className="font-medium text-black">John Smith</p>
                    <p className="text-sm text-gray-500">Content Creator</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Media Patterns */}
        <section id="media">
          <h2 className="font-wagner text-4xl tracking-tight mb-8 text-black">Media Patterns</h2>
          <p className="text-gray-600 mb-6">Aspect ratios and thumbnail patterns for social media content.</p>

          {/* Aspect Ratios */}
          <h3 className="text-xl font-semibold mb-4">Aspect Ratios</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            {[
              { ratio: '1:1', label: 'Square', width: 'w-24', height: 'h-24', use: 'Instagram, Feed' },
              { ratio: '4:5', label: 'Portrait', width: 'w-20', height: 'h-24', use: 'Instagram, FB' },
              { ratio: '16:9', label: 'Landscape', width: 'w-32', height: 'h-[72px]', use: 'YouTube, Twitter' },
              { ratio: '9:16', label: 'Story/Reel', width: 'w-14', height: 'h-24', use: 'Stories, TikTok' },
              { ratio: '2:3', label: 'Pinterest', width: 'w-16', height: 'h-24', use: 'Pinterest' },
            ].map((r) => (
              <div key={r.ratio} className="bg-white rounded-xl p-4 shadow-md text-center">
                <div className={`${r.width} ${r.height} bg-gray-200 rounded-lg mx-auto mb-3 flex items-center justify-center`}>
                  <span className="text-xs text-gray-500">{r.ratio}</span>
                </div>
                <p className="font-medium text-sm">{r.label}</p>
                <p className="text-xs text-gray-500">{r.use}</p>
              </div>
            ))}
          </div>

          {/* Media Thumbnails */}
          <h3 className="text-xl font-semibold mb-4">Media Thumbnails</h3>
          <div className="bg-white rounded-xl p-6 shadow-md">
            <div className="flex flex-wrap gap-4">
              {/* Image */}
              <div className="relative w-24 h-24 bg-gray-200 rounded-lg overflow-hidden">
                <div className="absolute inset-0 flex items-center justify-center">
                  <Icon name="image" size="lg" className="text-gray-400" ariaHidden />
                </div>
              </div>
              {/* Video */}
              <div className="relative w-24 h-24 bg-gray-800 rounded-lg overflow-hidden">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-10 h-10 rounded-full bg-white/30 flex items-center justify-center">
                    <div className="w-0 h-0 border-l-[12px] border-l-white border-y-[8px] border-y-transparent ml-1"></div>
                  </div>
                </div>
                <span className="absolute bottom-1 right-1 text-[10px] text-white bg-black/60 px-1 rounded">0:30</span>
              </div>
              {/* PDF */}
              <div className="relative w-24 h-24 bg-red-50 rounded-lg overflow-hidden border border-red-200">
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <Icon name="file" size="lg" className="text-red-500" ariaHidden />
                  <span className="text-xs text-red-600 font-medium mt-1">PDF</span>
                </div>
              </div>
              {/* Carousel */}
              <div className="relative w-24 h-24 bg-gray-200 rounded-lg overflow-hidden">
                <div className="absolute inset-0 flex items-center justify-center">
                  <Icon name="image" size="lg" className="text-gray-400" ariaHidden />
                </div>
                <span className="absolute top-1 right-1 text-[10px] text-white bg-black/60 px-1.5 py-0.5 rounded flex items-center gap-1">
                  <Icon name="image" size="xs" ariaHidden /> 5
                </span>
              </div>
              {/* Upload placeholder */}
              <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center hover:border-gray-400 cursor-pointer transition-colors">
                <Icon name="plus-circle" size="md" className="text-gray-400" ariaHidden />
                <span className="text-xs text-gray-500 mt-1">Add</span>
              </div>
            </div>
          </div>
        </section>

        {/* Tables */}
        <section id="tables">
          <h2 className="font-wagner text-4xl tracking-tight mb-8 text-black">Tables</h2>
          <p className="text-gray-600 mb-6">Data table pattern for post lists, analytics, and management views.</p>

          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Post</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Channels</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {[
                  { title: 'New product launch announcement', channels: ['LinkedIn', 'Facebook'], status: 'Published', date: 'Jan 24, 2026' },
                  { title: 'Behind the scenes at our office', channels: ['Instagram', 'TikTok'], status: 'Scheduled', date: 'Jan 28, 2026' },
                  { title: 'Customer success story', channels: ['LinkedIn'], status: 'Draft', date: '-' },
                ].map((row, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-4 py-4">
                      <p className="text-sm font-medium text-black truncate max-w-xs">{row.title}</p>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex gap-1">
                        {row.channels.map((c) => (
                          <span
                            key={c}
                            className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-bold"
                            style={{ backgroundColor: c === 'LinkedIn' ? '#0A66C2' : c === 'Facebook' ? '#1877F2' : c === 'Instagram' ? '#E4405F' : '#000' }}
                          >
                            {c[0]}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        row.status === 'Published' ? 'bg-green-50 text-green-700' :
                        row.status === 'Scheduled' ? 'bg-purple-50 text-purple-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {row.status}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500">{row.date}</td>
                    <td className="px-4 py-4 text-right">
                      <button className="text-gray-400 hover:text-gray-600">
                        <Icon name="pencil" size="sm" ariaHidden />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Tabs */}
        <section id="tabs">
          <h2 className="font-wagner text-4xl tracking-tight mb-8 text-black">Tabs & Navigation</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Underline Tabs */}
            <div className="bg-white rounded-xl p-6 shadow-md">
              <h3 className="text-lg font-semibold mb-4">Underline Tabs</h3>
              <div className="border-b border-gray-200">
                <nav className="flex gap-6">
                  <button className="pb-3 text-sm font-medium text-black border-b-2 border-black">All Posts</button>
                  <button className="pb-3 text-sm font-medium text-gray-500 hover:text-gray-700">Scheduled</button>
                  <button className="pb-3 text-sm font-medium text-gray-500 hover:text-gray-700">Published</button>
                  <button className="pb-3 text-sm font-medium text-gray-500 hover:text-gray-700">Drafts</button>
                </nav>
              </div>
            </div>

            {/* Pill Tabs */}
            <div className="bg-white rounded-xl p-6 shadow-md">
              <h3 className="text-lg font-semibold mb-4">Pill Tabs</h3>
              <div className="flex gap-2 bg-gray-100 p-1 rounded-lg w-fit">
                <button className="px-4 py-2 text-sm font-medium bg-white rounded-md shadow-sm">Week</button>
                <button className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-black">Month</button>
                <button className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-black">Year</button>
              </div>
            </div>

            {/* Search Input */}
            <div className="bg-white rounded-xl p-6 shadow-md">
              <h3 className="text-lg font-semibold mb-4">Search Input</h3>
              <div className="relative">
                <Icon name="search" size="sm" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" ariaHidden />
                <input
                  type="text"
                  placeholder="Search posts..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                />
              </div>
            </div>

            {/* Filter Chips */}
            <div className="bg-white rounded-xl p-6 shadow-md">
              <h3 className="text-lg font-semibold mb-4">Filter Chips</h3>
              <div className="flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-black text-white rounded-full text-sm">
                  LinkedIn
                  <button className="hover:bg-white/20 rounded-full p-0.5"><Icon name="cross" size="xs" ariaHidden /></button>
                </span>
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-black text-white rounded-full text-sm">
                  Scheduled
                  <button className="hover:bg-white/20 rounded-full p-0.5"><Icon name="cross" size="xs" ariaHidden /></button>
                </span>
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-gray-200 cursor-pointer">
                  + Add filter
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Tags & Labels */}
        <section id="tags">
          <h2 className="font-wagner text-4xl tracking-tight mb-8 text-black">Tags & Labels</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Hashtags */}
            <div className="bg-white rounded-xl p-6 shadow-md">
              <h3 className="text-lg font-semibold mb-4">Hashtags</h3>
              <div className="flex flex-wrap gap-2">
                <span className="text-[#0A66C2] text-sm">#marketing</span>
                <span className="text-[#0A66C2] text-sm">#socialmedia</span>
                <span className="text-[#0A66C2] text-sm">#contentcreator</span>
                <span className="text-[#0A66C2] text-sm">#digitalmarketing</span>
              </div>
            </div>

            {/* Post Labels */}
            <div className="bg-white rounded-xl p-6 shadow-md">
              <h3 className="text-lg font-semibold mb-4">Post Labels</h3>
              <div className="flex flex-wrap gap-2">
                <span className="px-2 py-1 bg-[#FF6565]/10 text-[#FF6565] text-xs font-medium rounded">Campaign A</span>
                <span className="px-2 py-1 bg-[#69C7C1]/10 text-[#69C7C1] text-xs font-medium rounded">Product Launch</span>
                <span className="px-2 py-1 bg-[#D0FF00]/30 text-black text-xs font-medium rounded">Urgent</span>
                <span className="px-2 py-1 bg-[#0A66C2]/10 text-[#0A66C2] text-xs font-medium rounded">Q1 2026</span>
              </div>
            </div>
          </div>
        </section>

        {/* File Upload */}
        <section id="upload">
          <h2 className="font-wagner text-4xl tracking-tight mb-8 text-black">File Upload</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Drop Zone */}
            <div className="bg-white rounded-xl p-6 shadow-md">
              <h3 className="text-lg font-semibold mb-4">Drop Zone</h3>
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-[#69C7C1] hover:bg-[#69C7C1]/5 transition-colors cursor-pointer">
                <Icon name="download" size="lg" className="text-gray-400 mx-auto mb-3" ariaHidden />
                <p className="text-sm font-medium text-gray-700 mb-1">Drop files here or click to upload</p>
                <p className="text-xs text-gray-500">PNG, JPG, MP4 up to 50MB</p>
              </div>
            </div>

            {/* Upload Progress */}
            <div className="bg-white rounded-xl p-6 shadow-md">
              <h3 className="text-lg font-semibold mb-4">Upload Progress</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-10 h-10 bg-gray-200 rounded flex items-center justify-center">
                    <Icon name="image" size="sm" className="text-gray-500" ariaHidden />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-black truncate">product-photo.jpg</p>
                    <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                      <div className="bg-[#69C7C1] h-1.5 rounded-full" style={{ width: '75%' }}></div>
                    </div>
                  </div>
                  <span className="text-xs text-gray-500">75%</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <div className="w-10 h-10 bg-green-100 rounded flex items-center justify-center">
                    <Icon name="tick-circle" size="sm" className="text-green-600" ariaHidden />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-black truncate">video-clip.mp4</p>
                    <p className="text-xs text-green-600">Uploaded successfully</p>
                  </div>
                  <button className="text-gray-400 hover:text-gray-600">
                    <Icon name="cross" size="sm" ariaHidden />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Interactive Components */}
        <section id="interactive">
          <h2 className="font-wagner text-4xl tracking-tight mb-8 text-black">Interactive Components</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Modal */}
            <div className="bg-white rounded-xl p-8 shadow-md">
              <h3 className="text-lg font-semibold mb-4">Modal (Dialog)</h3>
              <p className="text-sm text-gray-600 mb-4">Centered overlay for confirmations, forms, and alerts.</p>
              <Button variant="primary" onClick={() => setModalOpen(true)}>
                Open Modal
              </Button>
              <Modal
                open={modalOpen}
                onOpenChange={setModalOpen}
                title="Example Modal"
                footer={
                  <div className="flex gap-3 justify-end">
                    <Button variant="secondary" onClick={() => setModalOpen(false)}>
                      Cancel
                    </Button>
                    <Button variant="primary" onClick={() => setModalOpen(false)}>
                      Confirm
                    </Button>
                  </div>
                }
              >
                <p>This is an example modal dialog with a header, content area, and footer.</p>
              </Modal>
            </div>

            {/* Drawer */}
            <div className="bg-white rounded-xl p-8 shadow-md">
              <h3 className="text-lg font-semibold mb-4">Drawer (Side Panel)</h3>
              <p className="text-sm text-gray-600 mb-4">Slides from the side for editing, filters, and detail views.</p>
              <button
                onClick={() => setDrawerOpen(true)}
                className="px-6 py-2.5 bg-black text-white rounded-lg font-semibold text-sm border border-transparent hover:bg-white hover:text-black hover:border-black focus:bg-black focus:text-white focus:border-[#D0FF00] focus:outline-none transition-all"
              >
                Open Drawer
              </button>
            </div>

            {/* Tooltip */}
            <div className="bg-white rounded-xl p-8 shadow-md md:col-span-2">
              <h3 className="text-lg font-semibold mb-4">Tooltips</h3>
              <p className="text-sm text-gray-600 mb-4">Contextual information on hover. Try hovering:</p>
              <div className="flex gap-4 flex-wrap">
                <Tooltip content="This is a tooltip">
                  <button className="px-4 py-2 bg-black text-white rounded-lg text-sm font-medium">Hover me</button>
                </Tooltip>
                <Tooltip content="Appears above the element" side="top">
                  <button className="px-4 py-2 border-2 border-black text-black rounded-lg text-sm font-medium">Top</button>
                </Tooltip>
                <Tooltip content="Appears to the right" side="right">
                  <button className="px-4 py-2 border-2 border-black text-black rounded-lg text-sm font-medium">Right</button>
                </Tooltip>
                <Tooltip content="Appears below the element" side="bottom">
                  <button className="px-4 py-2 border-2 border-black text-black rounded-lg text-sm font-medium">Bottom</button>
                </Tooltip>
                <Tooltip content="Appears to the left" side="left">
                  <button className="px-4 py-2 border-2 border-black text-black rounded-lg text-sm font-medium">Left</button>
                </Tooltip>
              </div>
            </div>

            {/* Toast Notifications */}
            <div className="bg-white rounded-xl p-8 shadow-md md:col-span-2">
              <h3 className="text-lg font-semibold mb-4">Toast Notifications</h3>
              <p className="text-sm text-gray-600 mb-4">Temporary feedback messages.</p>
              <div className="flex gap-4 flex-wrap">
                <button
                  onClick={() => showToast('This is a success message!', 'success')}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700"
                >
                  Success Toast
                </button>
                <button
                  onClick={() => showToast('Something went wrong', 'error')}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700"
                >
                  Error Toast
                </button>
                <button
                  onClick={() => showToast('Please check your settings', 'warning')}
                  className="px-4 py-2 bg-yellow-500 text-black rounded-lg text-sm font-medium hover:bg-yellow-600"
                >
                  Warning Toast
                </button>
                <button
                  onClick={() => showToast('New features available', 'info')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                >
                  Info Toast
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Proposed Changes */}
        <section id="proposed">
          <h2 className="font-wagner text-4xl tracking-tight mb-4 text-black">Proposed Changes</h2>
          <p className="text-gray-600 mb-8">Design refinements under consideration for brand alignment</p>

          {/* More Black */}
          <div className="mb-12">
            <h3 className="text-xl font-semibold mb-4">1. Use More Black Overall</h3>
            <p className="text-sm text-gray-600 mb-6">
              The brand guidelines list Black as a primary color. Currently we lean heavily on coral/teal.
              Adding more black will create stronger contrast and align with brand spec.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Current */}
              <div className="bg-white rounded-xl p-6 shadow-md">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-4">Current</p>
                <div className="space-y-3">
                  <h4 className="text-2xl font-bold text-black">Section Header</h4>
                  <p className="text-gray-600">Body text in gray-600</p>
                  <p className="text-sm text-gray-500">Small text in gray-500</p>
                </div>
              </div>
              {/* Proposed */}
              <div className="bg-white rounded-xl p-6 shadow-md border-2 border-[#D0FF00]">
                <p className="text-xs text-[#D0FF00] uppercase tracking-wide mb-4 font-semibold">Proposed</p>
                <div className="space-y-3">
                  <h4 className="text-2xl font-bold text-black">Section Header</h4>
                  <p className="text-black">Body text in gray-800</p>
                  <p className="text-sm text-gray-600">Small text in gray-600</p>
                </div>
              </div>
            </div>
          </div>

          {/* Button Differentiation */}
          <div className="mb-12">
            <h3 className="text-xl font-semibold mb-4">2. Button Differentiation by Background</h3>
            <p className="text-sm text-gray-600 mb-6">
              Buttons should adapt to their background context. More variety needed: solid black, outlined,
              ghost buttons, and proper contrast on different backgrounds.
            </p>

            {/* On Light/Gradient Backgrounds */}
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-3">On Light/Gradient Backgrounds</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Current */}
              <div className="rounded-xl p-6 shadow-md" style={{ background: 'linear-gradient(180deg, #FFFFFF 0%, #FFFFFF 50%, #D5EEEB 100%)' }}>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-4">Current</p>
                <div className="flex flex-wrap gap-3">
                  <Button variant="primary">Primary</Button>
                  <Button variant="secondary">Secondary</Button>
                </div>
              </div>
              {/* Proposed */}
              <div className="rounded-xl p-6 shadow-md border-2 border-[#D0FF00]" style={{ background: 'linear-gradient(180deg, #FFFFFF 0%, #FFFFFF 50%, #D5EEEB 100%)' }}>
                <p className="text-xs text-[#D0FF00] uppercase tracking-wide mb-4 font-semibold">Proposed</p>
                <div className="flex flex-wrap gap-3">
                  <button className="px-4 py-2 bg-black text-white rounded-lg font-semibold text-sm border border-transparent hover:bg-white hover:text-black hover:border-black focus:bg-black focus:text-white focus:border-[#D0FF00] focus:outline-none transition-colors">
                    Black Solid
                  </button>
                  <button className="px-4 py-2 bg-transparent border-2 border-black text-black rounded-lg font-semibold text-sm hover:bg-black/10 transition-colors">
                    Black Outline
                  </button>
                  <button className="px-4 py-2 bg-transparent text-black rounded-lg font-semibold text-sm hover:bg-black/5 transition-colors">
                    Ghost
                  </button>
                </div>
              </div>
            </div>

            {/* On Dark Backgrounds */}
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-3">On Dark Backgrounds</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Current */}
              <div className="rounded-xl p-6 shadow-md bg-[#074F47]">
                <p className="text-xs text-white/60 uppercase tracking-wide mb-4">Current - Poor contrast</p>
                <div className="flex flex-wrap gap-3">
                  <Button variant="primary">Primary</Button>
                  <Button variant="secondary">Secondary</Button>
                </div>
              </div>
              {/* Proposed */}
              <div className="rounded-xl p-6 shadow-md bg-[#074F47] border-2 border-[#D0FF00]">
                <p className="text-xs text-[#D0FF00] uppercase tracking-wide mb-4 font-semibold">Proposed - Proper contrast</p>
                <div className="flex flex-wrap gap-3">
                  <button className="px-4 py-2 bg-white text-[#074F47] rounded-lg font-semibold text-sm hover:bg-gray-100 transition-colors">
                    White Solid
                  </button>
                  <button className="px-4 py-2 bg-transparent border-2 border-white text-white rounded-lg font-semibold text-sm hover:bg-white/10 transition-colors">
                    White Outline
                  </button>
                  <button className="px-4 py-2 bg-[#D0FF00] text-black rounded-lg font-semibold text-sm hover:bg-[#bce600] transition-colors">
                    Lime Accent
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Non-Rectangle Media */}
          <div className="mb-12">
            <h3 className="text-xl font-semibold mb-4">3. Media Formats - Not Always Rectangles</h3>
            <p className="text-sm text-gray-600 mb-6">
              Media thumbnails can use varied shapes for visual interest. Rounded corners, circles for avatars,
              or organic shapes for featured content.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Current */}
              <div className="bg-white rounded-xl p-6 shadow-md">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-4">Current - Sharp rectangles</p>
                <div className="flex gap-4">
                  <div className="w-24 h-24 bg-gray-300 rounded-md"></div>
                  <div className="w-24 h-24 bg-gray-300 rounded-md"></div>
                  <div className="w-24 h-24 bg-gray-300 rounded-md"></div>
                </div>
              </div>
              {/* Proposed */}
              <div className="bg-white rounded-xl p-6 shadow-md border-2 border-[#D0FF00]">
                <p className="text-xs text-[#D0FF00] uppercase tracking-wide mb-4 font-semibold">Proposed - Varied shapes</p>
                <div className="flex gap-4 items-center">
                  <div className="w-24 h-24 bg-gray-300 rounded-2xl"></div>
                  <div className="w-20 h-20 bg-gray-300 rounded-full"></div>
                  <div className="w-24 h-24 bg-gray-300 rounded-3xl"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Black Input Outlines */}
          <div className="mb-12">
            <h3 className="text-xl font-semibold mb-4">4. Black Outlines on Input Fields</h3>
            <p className="text-sm text-gray-600 mb-6">
              Replacing light gray borders with black outlines creates stronger definition and aligns with
              the brand's use of black as a primary color.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Current */}
              <div className="bg-white rounded-xl p-6 shadow-md">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-4">Current - Light gray borders</p>
                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder="Email address"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <input
                    type="text"
                    placeholder="Password"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
              {/* Proposed */}
              <div className="bg-white rounded-xl p-6 shadow-md border-2 border-[#D0FF00]">
                <p className="text-xs text-[#D0FF00] uppercase tracking-wide mb-4 font-semibold">Proposed - Black outline on focus</p>
                <p className="text-xs text-gray-500 mb-3">Normal state: gray border. Focus: black outline appears</p>
                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder="Email address (click to see focus)"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-gray-300 focus:outline focus:outline-2 focus:outline-gray-300"
                  />
                  <input
                    type="text"
                    placeholder="Password (click to see focus)"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-gray-300 focus:outline focus:outline-2 focus:outline-gray-300"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Onboarding Experience Mockup */}
          <div className="mb-12">
            <h3 className="text-xl font-semibold mb-4">5. Onboarding Experience</h3>
            <p className="text-sm text-gray-600 mb-6">
              Modern onboarding like established SaaS apps - persistent sidebar checklist with progress tracking.
            </p>
            <div className="bg-gray-100 rounded-xl p-4 shadow-md">
              <div className="flex gap-4">
                {/* Main content area mock */}
                <div className="flex-1 bg-white rounded-lg p-6 min-h-[300px]">
                  <p className="text-gray-400 text-sm">Main App Content Area</p>
                </div>

                {/* Onboarding sidebar */}
                <div className="w-80 bg-white rounded-xl shadow-lg p-5 border-2 border-[#D0FF00]">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-black">Getting Started</h4>
                    <span className="text-xs bg-[#D0FF00] text-black px-2 py-1 rounded-full font-medium">3/6</span>
                  </div>

                  {/* Progress bar */}
                  <div className="w-full bg-gray-200 rounded-full h-2 mb-5">
                    <div className="bg-[#69C7C1] h-2 rounded-full" style={{ width: '50%' }}></div>
                  </div>

                  {/* Checklist items */}
                  <ul className="space-y-3">
                    <li className="flex items-center gap-3 p-2 rounded-lg bg-green-50">
                      <span className="w-6 h-6 rounded-full bg-[#69C7C1] text-white flex items-center justify-center">
                        <Icon name="tick-circle" size="sm" ariaHidden />
                      </span>
                      <span className="text-sm text-gray-500 line-through">Create your account</span>
                    </li>
                    <li className="flex items-center gap-3 p-2 rounded-lg bg-green-50">
                      <span className="w-6 h-6 rounded-full bg-[#69C7C1] text-white flex items-center justify-center">
                        <Icon name="tick-circle" size="sm" ariaHidden />
                      </span>
                      <span className="text-sm text-gray-500 line-through">Set up your Brand</span>
                    </li>
                    <li className="flex items-center gap-3 p-2 rounded-lg bg-green-50">
                      <span className="w-6 h-6 rounded-full bg-[#69C7C1] text-white flex items-center justify-center">
                        <Icon name="tick-circle" size="sm" ariaHidden />
                      </span>
                      <span className="text-sm text-gray-500 line-through">Connect a channel</span>
                    </li>
                    <li className="flex items-center gap-3 p-2 rounded-lg bg-[#FF6565]/10 border border-[#FF6565]/30">
                      <span className="w-6 h-6 rounded-full bg-[#FF6565] text-white flex items-center justify-center text-xs font-bold">4</span>
                      <span className="text-sm font-medium text-black">Create your first post</span>
                      <Icon name="angle-right" size="sm" className="ml-auto text-[#FF6565]" ariaHidden />
                    </li>
                    <li className="flex items-center gap-3 p-2 rounded-lg">
                      <span className="w-6 h-6 rounded-full bg-gray-200 text-gray-500 flex items-center justify-center text-xs font-bold">5</span>
                      <span className="text-sm text-gray-500">Schedule a post</span>
                    </li>
                    <li className="flex items-center gap-3 p-2 rounded-lg">
                      <span className="w-6 h-6 rounded-full bg-gray-200 text-gray-500 flex items-center justify-center text-xs font-bold">6</span>
                      <span className="text-sm text-gray-500">Invite a team member</span>
                    </li>
                  </ul>

                  <button className="mt-4 w-full text-xs text-gray-500 hover:text-gray-700">
                    Dismiss for now
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Help & Info Patterns */}
          <div className="mb-12">
            <h3 className="text-xl font-semibold mb-4">6. Help & Info Patterns</h3>
            <p className="text-sm text-gray-600 mb-6">
              Contextual help patterns used throughout the app.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Info Box */}
              <div className="bg-white rounded-xl p-6 shadow-md">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-4">Info Box</p>
                <div className="bg-soft-mint border border-light-teal-gray rounded-lg p-4 flex gap-3">
                  <Icon name="info" size="md" className="text-dark-teal flex-shrink-0" ariaHidden />
                  <div>
                    <p className="text-sm font-medium text-dark-teal">Pro Tip</p>
                    <p className="text-sm text-dark-teal">You can schedule posts up to 6 months in advance.</p>
                  </div>
                </div>
              </div>

              {/* Warning Box */}
              <div className="bg-white rounded-xl p-6 shadow-md">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-4">Warning Box</p>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex gap-3">
                  <Icon name="alarm-clock" size="md" className="text-yellow-600 flex-shrink-0" ariaHidden />
                  <div>
                    <p className="text-sm font-medium text-yellow-800">Heads up</p>
                    <p className="text-sm text-yellow-700">Your trial expires in 3 days.</p>
                  </div>
                </div>
              </div>

              {/* Inline Help */}
              <div className="bg-white rounded-xl p-6 shadow-md">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-4">Inline Help (Label + Tooltip)</p>
                <div className="flex items-center gap-2 mb-2">
                  <label className="text-sm font-medium text-gray-700">Engagement Rate</label>
                  <Tooltip content="The percentage of people who interacted with your post compared to total reach.">
                    <button className="w-4 h-4 rounded-full bg-gray-200 text-gray-600 text-xs flex items-center justify-center hover:bg-gray-300">?</button>
                  </Tooltip>
                </div>
                <p className="text-2xl font-bold text-black">4.2%</p>
              </div>

              {/* Dismissable Tip */}
              <div className="bg-white rounded-xl p-6 shadow-md">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-4">Dismissable Tip</p>
                <div className="bg-[#D5EEEB] rounded-lg p-4 flex items-start gap-3 relative">
                  <Icon name="star" size="md" className="text-[#074F47] flex-shrink-0" ariaHidden />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-[#074F47]">Did you know?</p>
                    <p className="text-sm text-[#074F47]/80">Posts with images get 2.3x more engagement.</p>
                  </div>
                  <button className="text-[#074F47]/60 hover:text-[#074F47]">
                    <Icon name="cross" size="sm" ariaHidden />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Icons on Dark Note */}
          <div className="mb-12">
            <h3 className="text-xl font-semibold mb-4">7. Icons on Dark Backgrounds</h3>
            <p className="text-sm text-gray-600 mb-6">
              Note: Thin line icons (HappyIcons) have visibility issues on dark backgrounds.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-[#074F47] rounded-xl p-6">
                <p className="text-xs text-white/60 uppercase tracking-wide mb-4">Problem - Barely visible</p>
                <div className="flex gap-4">
                  <Icon name="heart" size="lg" className="text-white/40" ariaHidden />
                  <Icon name="calendar" size="lg" className="text-white/40" ariaHidden />
                  <Icon name="user" size="lg" className="text-white/40" ariaHidden />
                  <Icon name="tools" size="lg" className="text-white/40" ariaHidden />
                </div>
              </div>
              <div className="bg-[#074F47] rounded-xl p-6 border-2 border-[#D0FF00]">
                <p className="text-xs text-[#D0FF00] uppercase tracking-wide mb-4 font-semibold">Solutions</p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Icon name="heart" size="lg" className="text-white" ariaHidden />
                    <span className="text-sm text-white">Full white (text-white)</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Icon name="calendar" size="lg" className="text-[#D0FF00]" ariaHidden />
                    <span className="text-sm text-white">Accent color (lime)</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                      <Icon name="user" size="md" className="text-white" ariaHidden />
                    </span>
                    <span className="text-sm text-white">With background circle</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Loading & Empty States */}
          <div className="mb-12">
            <h3 className="text-xl font-semibold mb-4">8. Loading & Empty States</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Loading Skeleton */}
              <div className="bg-white rounded-xl p-6 shadow-md">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-4">Loading Skeleton</p>
                <div className="space-y-3 animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                  <div className="h-20 bg-gray-200 rounded"></div>
                  <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                </div>
              </div>

              {/* Empty State */}
              <div className="bg-white rounded-xl p-6 shadow-md">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-4">Empty State</p>
                <div className="text-center py-6">
                  <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                    <Icon name="calendar" size="lg" className="text-gray-400" ariaHidden />
                  </div>
                  <p className="text-black font-medium mb-1">No posts yet</p>
                  <p className="text-sm text-gray-500 mb-4">Create your first post to get started</p>
                  <button className="px-4 py-2 bg-black text-white rounded-lg text-sm font-medium">
                    Create Post
                  </button>
                </div>
              </div>

              {/* Progress Indicator */}
              <div className="bg-white rounded-xl p-6 shadow-md">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-4">Progress States</p>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Uploading...</span>
                      <span>65%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-[#FF6565] h-2 rounded-full" style={{ width: '65%' }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Processing</span>
                      <span>100%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-[#69C7C1] h-2 rounded-full w-full"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Feature Roadmap */}
          <div>
            <h3 className="text-xl font-semibold mb-4">9. Engagement Features (Roadmap)</h3>
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { title: 'Milestones', desc: 'Achievement badges & progress', priority: 'high' },
                  { title: 'Contextual Tips', desc: '"Have you tried bulk upload?"', priority: 'high' },
                  { title: 'Dashboard Tips', desc: '"Did you know?" suggestions', priority: 'medium' },
                  { title: 'Consultative', desc: 'Industry-specific recommendations', priority: 'medium' },
                  { title: 'Toggle Tooltips', desc: 'Disable hints when expert', priority: 'low' },
                  { title: 'Gamification', desc: 'Streaks, rewards, leaderboards', priority: 'low' },
                ].map((item, i) => (
                  <div key={i} className="p-4 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`w-2 h-2 rounded-full ${
                        item.priority === 'high' ? 'bg-[#FF6565]' :
                        item.priority === 'medium' ? 'bg-[#69C7C1]' : 'bg-gray-400'
                      }`}></span>
                      <p className="font-medium text-black">{item.title}</p>
                    </div>
                    <p className="text-sm text-gray-500">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

      </main>

      {/* Drawer Demo */}
      <Dialog.Root open={drawerOpen} onOpenChange={setDrawerOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-40" />
          <Dialog.Content className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-xl z-50 animate-slide-in-from-right">
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between p-4 border-b">
                <Dialog.Title className="text-lg font-semibold">Example Drawer</Dialog.Title>
                <Dialog.Close className="p-2 hover:bg-gray-100 rounded-lg">
                  <Icon name="cross" size="sm" ariaHidden />
                </Dialog.Close>
              </div>
              <div className="flex-1 p-6 overflow-auto">
                <p className="text-gray-600 mb-4">
                  Drawers slide in from the side and are used for:
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center gap-2">
                    <Icon name="tick-circle" size="sm" className="text-[#69C7C1]" ariaHidden />
                    Post editing with side-by-side preview
                  </li>
                  <li className="flex items-center gap-2">
                    <Icon name="tick-circle" size="sm" className="text-[#69C7C1]" ariaHidden />
                    Filter panels
                  </li>
                  <li className="flex items-center gap-2">
                    <Icon name="tick-circle" size="sm" className="text-[#69C7C1]" ariaHidden />
                    Detail views without leaving context
                  </li>
                  <li className="flex items-center gap-2">
                    <Icon name="tick-circle" size="sm" className="text-[#69C7C1]" ariaHidden />
                    Form panels
                  </li>
                </ul>
              </div>
              <div className="p-4 border-t flex gap-3 justify-end">
                <button
                  onClick={() => setDrawerOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => setDrawerOpen(false)}
                  className="px-4 py-2 bg-black text-white rounded-lg text-sm font-medium border border-transparent hover:bg-white hover:text-black hover:border-black focus:bg-black focus:text-white focus:border-[#D0FF00] focus:outline-none"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Footer */}
      <footer className="bg-[#074F47] mt-20 py-12">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-white text-sm">Ägile Ässets Design System • {new Date().getFullYear()}</p>
          <p className="text-white/60 text-xs mt-2">Brand Guidelines Reference for Internal Use</p>
        </div>
      </footer>
    </div>
    </TooltipProvider>
  );
};
